import {useEffect, useState} from 'react';
import useYDocStore from '../../stores/useYDocStore';
import {buildThreadId, getCommentCount, observeThread} from '../prosemirror-editor/yMaps/commentsYMap';

/**
 * Subscribe to the live Yjs comments thread and return a stable count.
 *
 * Replaces `useCommentsCount` (RTDB-based) for the editor comment UI.
 *
 * Important: `count` may be 0 while still loading. Use `isLoaded` to
 * distinguish "empty thread" from "not loaded yet".
 *
 * @param {{ elementName: string, elementId: string }} params
 * @returns {{ count: number, isLoaded: boolean }}
 */
export function useYCommentsCount({elementName, elementId}) {
    const yComments = useYDocStore((s) => s.yComments);

    const [count, setCount] = useState(0);
    // isLoaded becomes true once yComments is available (even if the thread is empty)
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        if (!elementName || !elementId || !yComments) {
            setCount(0);
            setIsLoaded(false);
            return;
        }

        const threadId = buildThreadId(elementName, elementId);

        // Load initial count immediately
        setCount(getCommentCount(yComments, threadId));
        setIsLoaded(true);

        // Stay reactive — re-read count on every thread change
        const unsubscribe = observeThread(yComments, threadId, () => {
            setCount(getCommentCount(yComments, threadId));
        });

        return () => unsubscribe();
    }, [elementName, elementId, yComments]);

    return {count, isLoaded};
}

